/**
 * Cupom pendente capturado por deep-link (`/{slug}?cupom=CODIGO`) — TASK-004.
 *
 * Quando o cliente abre o app a partir de um link de divulgação com o cupom já
 * embutido na URL, guardamos o código aqui para que ele seja aplicado mais
 * adiante no fluxo de pedido (outra fatia da issue). A persistência usa
 * `sessionStorage`: o cupom vale para a sessão atual — sobrevive a uma eventual
 * ida ao login — e some ao fechar a aba, sem "vazar" para sessões futuras.
 *
 * Segurança (OWASP Top 10 — 2021):
 * - A03 (Injection): o código vem de entrada não confiável (URL) e é validado
 *   contra uma allowlist estrita antes de ser persistido. Isso impede que
 *   caracteres de path/query (`/`, `?`, `#`, `..`) cheguem ao endpoint de
 *   resgate, que interpola o código diretamente no path.
 * - Robustez: todo acesso ao storage é envolto em try/catch para tolerar modo
 *   privado, cota excedida ou ambientes sem storage.
 * - Privacidade: guardamos apenas o código do cupom, que não é dado pessoal.
 */

const STORAGE_KEY = 'larah:pending-coupon';

/**
 * Allowlist de código de cupom: letras, dígitos, hífen e underline, de 1 a 40
 * caracteres. Cobre os formatos de cupom usados no resgate sem permitir barras,
 * pontos ou caracteres de controle que poderiam desviar o path do endpoint.
 */
const COUPON_CODE_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;

/**
 * Valida e normaliza um código de cupom vindo de entrada não confiável (URL).
 *
 * @returns O código com espaços das bordas removidos quando válido, ou `null`.
 */
export function sanitizeCouponCode(code: string | null | undefined): string | null {
  if (!code) return null;

  const normalized = code.trim();
  if (!COUPON_CODE_PATTERN.test(normalized)) return null;

  return normalized;
}

/**
 * Persiste o cupom pendente da sessão.
 *
 * @returns O código normalizado quando salvo, ou `null` se o código é inválido.
 */
export function savePendingCoupon(code: string | null | undefined): string | null {
  const safeCode = sanitizeCouponCode(code);
  if (!safeCode) return null;

  try {
    sessionStorage.setItem(STORAGE_KEY, safeCode);
  } catch {
    // Storage indisponível — segue sem persistir.
  }

  return safeCode;
}

/** Retorna o cupom pendente da sessão, ou `null` se não houver/for inválido. */
export function getPendingCoupon(): string | null {
  try {
    return sanitizeCouponCode(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Remove o cupom pendente da sessão. */
export function clearPendingCoupon(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage indisponível — ignora.
  }
}
